/**
 * Audio Service for Poker Reborn
 *
 * Handles sound effects and music playback.
 */

import { EventEmitter } from './EventEmitter';
import { SOUNDS } from '@/config/assets';
import { log } from '@/utils/log';
import { storageService } from './StorageService';
import { appState } from '@/state/AppState';
import type { SoundCategory, SoundEffect } from '@/types/ui';

// =============================================================================
// TYPES
// =============================================================================

/** Audio service events */
export interface AudioEvents {
  /** Sound started playing */
  play: { sound: string };

  /** Sound stopped */
  stop: { sound: string };

  /** Volume changed */
  volumeChange: { category: SoundCategory; volume: number };

  /** Mute state changed */
  muteChange: { muted: boolean };
}

/** Sound instance */
interface SoundInstance {
  audio: HTMLAudioElement;
  category: SoundCategory;
}

// =============================================================================
// AUDIO SERVICE
// =============================================================================

export class AudioService extends EventEmitter<AudioEvents> {
  private static instance: AudioService | null = null;

  private sounds: Map<string, HTMLAudioElement[]> = new Map();
  private activeSounds: Map<string, SoundInstance> = new Map();

  private volumes: Record<SoundCategory, number> = {
    sfx: 1.0,
    music: 0.5,
    ambient: 0.3,
    voice: 1.0,
  };

  private masterVolume = 1.0;
  private muted = false;
  private initialized = false;
  private audioUnlocked = false;

  private constructor() {
    super();
    // Load settings immediately so muted state is correct before init()
    const soundSettings = storageService.getSoundSettings();
    this.masterVolume = soundSettings.master;
    this.muted = soundSettings.muted;
  }

  /** Get singleton instance */
  static getInstance(): AudioService {
    if (!AudioService.instance) {
      AudioService.instance = new AudioService();
    }
    return AudioService.instance;
  }

  /**
   * Initialize audio service
   * Should be called after user interaction (browser autoplay policy)
   */
  async init(): Promise<void> {
    if (this.initialized) return;

    // Load saved settings from storage
    this.loadSettings();

    // Setup audio unlock for mobile (iOS WKWebView blocks audio until user gesture)
    this.setupAudioUnlock();

    // Preload common sounds
    await this.preloadSounds();

    this.initialized = true;
    log.audio.info`Audio service initialized (master: ${this.masterVolume}, muted: ${this.muted})`;
  }

  /**
   * Load audio settings from storage
   */
  private loadSettings(): void {
    const soundSettings = storageService.getSoundSettings();
    this.masterVolume = soundSettings.master;
    this.muted = soundSettings.muted;
  }

  /**
   * Play a sound effect
   *
   * Game sounds (cards, chips, actions) only play when at a table.
   * UI sounds (buttons, popups) play anywhere.
   */
  play(sound: SoundEffect, category: SoundCategory = 'sfx'): void {
    if (this.muted) {
      log.audio.debug`Sound muted, skipping: ${sound}`;
      return;
    }

    // Screen-based filtering: Only play game sounds when at table
    // UI sounds (button_click, pop, popup_open, popup_close) play anywhere
    const uiSounds: SoundEffect[] = ['button_click', 'pop', 'popup_open', 'popup_close'];
    const isUiSound = uiSounds.includes(sound);
    const currentScreen = appState.currentScreen.get();

    if (!isUiSound && currentScreen !== 'table') {
      log.audio.debug`Game sound blocked (not at table): ${sound}`;
      return;
    }

    const soundPath = this.getSoundPath(sound);
    if (!soundPath) {
      log.audio.warning`Sound not found: ${sound}`;
      return;
    }

    this.playSound(soundPath, category);
  }

  /**
   * Play a random sound from a list
   */
  playRandom(sounds: SoundEffect[], category: SoundCategory = 'sfx'): void {
    if (sounds.length === 0) return;

    const index = Math.floor(Math.random() * sounds.length);
    const sound = sounds[index];
    if (sound) {
      this.play(sound, category);
    }
  }

  /**
   * Stop a playing sound
   */
  stop(sound: string): void {
    const instance = this.activeSounds.get(sound);
    if (instance) {
      instance.audio.pause();
      instance.audio.currentTime = 0;
      this.activeSounds.delete(sound);
      this.emit('stop', { sound });
    }
  }

  /**
   * Stop all sounds
   */
  stopAll(): void {
    this.activeSounds.forEach((instance, sound) => {
      instance.audio.pause();
      instance.audio.currentTime = 0;
      this.emit('stop', { sound });
    });
    this.activeSounds.clear();
  }

  /**
   * Set master volume
   */
  setMasterVolume(volume: number): void {
    this.masterVolume = Math.max(0, Math.min(1, volume));
    this.updateAllVolumes();
  }

  /**
   * Get master volume
   */
  getMasterVolume(): number {
    return this.masterVolume;
  }

  /**
   * Set category volume
   */
  setCategoryVolume(category: SoundCategory, volume: number): void {
    this.volumes[category] = Math.max(0, Math.min(1, volume));
    this.updateCategoryVolume(category);
    this.emit('volumeChange', { category, volume: this.volumes[category] });
  }

  /**
   * Get category volume
   */
  getCategoryVolume(category: SoundCategory): number {
    return this.volumes[category];
  }

  /**
   * Set muted state
   */
  setMuted(muted: boolean): void {
    this.muted = muted;
    this.emit('muteChange', { muted });

    if (muted) {
      this.stopAll();
    }
  }

  /**
   * Get muted state
   */
  isMuted(): boolean {
    return this.muted;
  }

  /**
   * Toggle mute
   */
  toggleMute(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  // ===========================================================================
  // CONVENIENCE METHODS
  // ===========================================================================

  /** Play deal card sound */
  playDeal(): void {
    this.playRandom(['deal1', 'deal2', 'deal3', 'deal4', 'deal5', 'deal6']);
  }

  /** Play chip sound */
  playChip(): void {
    this.playRandom(['chip1', 'chip2', 'chip3', 'chip4']);
  }

  /** Play fold sound */
  playFold(): void {
    this.playRandom(['fold1', 'fold2', 'fold3']);
  }

  /** Play check sound */
  playCheck(): void {
    this.playRandom(['check1', 'check2', 'check3']);
  }

  /** Play flop sound */
  playFlop(): void {
    this.playRandom(['flop1', 'flop2', 'flop3']);
  }

  /** Play turn/river sound */
  playTurn(): void {
    this.playRandom(['turn1', 'turn2', 'turn3', 'turn4', 'turn5', 'turn6']);
  }

  /** Play action bell (your turn) */
  playActionBell(): void {
    this.play('act');
  }

  /** Play win sound */
  playWin(): void {
    this.play('chips_win');
  }

  /** Play all-in sound */
  playAllin(): void {
    this.playRandom(['allin1', 'allin2', 'allin3']);
  }

  /** Play applause sound */
  playApplause(): void {
    this.play('applause');
  }

  /** Play timer tick sound */
  playTick(): void {
    this.play('tick');
  }

  /** Play knock (alternative check) sound */
  playKnock(): void {
    this.playRandom(['knock1', 'knock2', 'knock3']);
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  /**
   * Unlock audio playback on mobile devices.
   * iOS WKWebView (and some Android WebViews) block audio until a user gesture.
   * We play a silent buffer on first touch to unlock the audio pipeline.
   */
  private setupAudioUnlock(): void {
    if (this.audioUnlocked) return;

    const unlockHandler = (): void => {
      if (this.audioUnlocked) return;

      const silentAudio = new Audio();
      silentAudio.src = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';
      silentAudio.play().then(() => {
        silentAudio.pause();
        this.audioUnlocked = true;
        log.audio.info`Audio unlocked via user gesture`;
        window.removeEventListener('touchstart', unlockHandler);
        window.removeEventListener('touchend', unlockHandler);
        window.removeEventListener('click', unlockHandler);
      }).catch(() => {
        // Ignore — will retry on next gesture
      });
    };

    window.addEventListener('touchstart', unlockHandler, { passive: true });
    window.addEventListener('touchend', unlockHandler, { passive: true });
    window.addEventListener('click', unlockHandler, { passive: true });
  }

  private async preloadSounds(): Promise<void> {
    // Preload common sound effects
    const commonSounds = [
      ...SOUNDS.deal,
      ...SOUNDS.chip,
      SOUNDS.act,
      SOUNDS.chipsWin,
    ];

    await Promise.all(
      commonSounds.map((path) => this.loadSound(path))
    );
  }

  private async loadSound(path: string): Promise<HTMLAudioElement> {
    return new Promise((resolve, reject) => {
      const audio = new Audio(path);
      audio.preload = 'auto';

      audio.oncanplaythrough = () => { resolve(audio); };
      audio.onerror = () => { reject(new Error(`Failed to load: ${path}`)); };

      // Store for reuse
      if (!this.sounds.has(path)) {
        this.sounds.set(path, []);
      }
      this.sounds.get(path)!.push(audio);
    });
  }

  private playSound(path: string, category: SoundCategory): void {
    // Final guard - never play if muted
    if (this.muted) return;

    // Get or create audio element
    let audio: HTMLAudioElement;

    const pool = this.sounds.get(path);
    if (pool && pool.length > 0) {
      // Find an available audio element from pool
      audio = pool.find((a) => a.paused || a.ended) || new Audio(path);
    } else {
      audio = new Audio(path);
    }

    // Set volume
    audio.volume = this.masterVolume * this.volumes[category];

    // Play
    audio.currentTime = 0;
    audio.play().catch((error) => {
      log.audio.warning`Audio play failed: ${error}`;
    });

    // Track active sound
    const id = `${path}_${Date.now()}`;
    this.activeSounds.set(id, { audio, category });

    // Clean up when ended
    audio.onended = () => {
      this.activeSounds.delete(id);
    };

    this.emit('play', { sound: path });
  }

  private getSoundPath(sound: SoundEffect): string | null {
    // Map sound effect names to paths
    const soundMap: Record<string, string | undefined> = {
      // Card sounds
      deal1: SOUNDS.deal[0],
      deal2: SOUNDS.deal[1],
      deal3: SOUNDS.deal[2],
      deal4: SOUNDS.deal[3],
      deal5: SOUNDS.deal[4],
      deal6: SOUNDS.deal[5],
      fold1: SOUNDS.fold[0],
      fold2: SOUNDS.fold[1],
      fold3: SOUNDS.fold[2],
      flop1: SOUNDS.flop[0],
      flop2: SOUNDS.flop[1],
      flop3: SOUNDS.flop[2],
      flop4: SOUNDS.flop[3],
      turn1: SOUNDS.turn[0],
      turn2: SOUNDS.turn[1],
      turn3: SOUNDS.turn[2],
      turn4: SOUNDS.turn[3],
      turn5: SOUNDS.turn[4],
      turn6: SOUNDS.turn[5],

      // Chip sounds
      chip1: SOUNDS.chip[0],
      chip2: SOUNDS.chip[1],
      chip3: SOUNDS.chip[2],
      chip4: SOUNDS.chip[3],
      chips_win: SOUNDS.chipsWin,
      chips_end: SOUNDS.chipsEnd,

      // Action sounds
      check1: SOUNDS.check[0],
      check2: SOUNDS.check[1],
      check3: SOUNDS.check[2],
      knock1: SOUNDS.knock[0],
      knock2: SOUNDS.knock[1],
      knock3: SOUNDS.knock[2],
      allin1: SOUNDS.allin[0],
      allin2: SOUNDS.allin[1],
      allin3: SOUNDS.allin[2],

      // Alert sounds
      act: SOUNDS.act,
      second_act: SOUNDS.second_act,
      tick: SOUNDS.tick,

      // UI sounds
      button_click: SOUNDS.buttonClick,
      pop: SOUNDS.pop,
      popup_open: SOUNDS.popupOpen,
      popup_close: SOUNDS.popupClose,

      // Rewards & celebrations
      bonus: SOUNDS.bonus,
      applause: SOUNDS.applause,

      // Ambient & music
      room_noise: SOUNDS.roomNoise,
      music_groove: SOUNDS.musicGroove,
    };

    return soundMap[sound] ?? null;
  }

  private updateAllVolumes(): void {
    this.activeSounds.forEach((instance) => {
      instance.audio.volume = this.masterVolume * this.volumes[instance.category];
    });
  }

  private updateCategoryVolume(category: SoundCategory): void {
    this.activeSounds.forEach((instance) => {
      if (instance.category === category) {
        instance.audio.volume = this.masterVolume * this.volumes[category];
      }
    });
  }
}

/** Singleton audio service instance */
export const audioService = AudioService.getInstance();
